/**
 * Wall trims: what a wall material lays along its walls beyond its 32px pieces, drawn by the client
 * over each piece. A piece repeats every cell; trims do not.
 * - masonry: stones laid by world position along each storey's face, columns of their own widths split
 *   into stones of their own heights, so courses break and joints never fall on the tile grid;
 * - coping: the back edge of a top nothing stands on, laid as stones of uneven length and set;
 * - weeds: tufts rooted along such a top's back edge;
 * - streaks: water stains down a face under a top nothing stands on;
 * - foot: rubble, tufts and moss where a ground-storey face meets the ground (they spill past the cell);
 * - spurs: a battered spur of stone hugging each outer corner at the foot (past the cell too);
 * - arch: a gateway the storey above walls over becomes an arch cut up into that storey's face -- where that face
 *   is seen from outside (no floor of that storey in front of it), not inside a room over the doorway;
 * - creepers: ivy that grows from a root at the foot of a face and climbs only what it can hold -- a
 *   face, and the face of the storey above only where that storey's wall stands on this one; where it
 *   does not, the stem reaches this storey's coping, runs along it and stops;
 * - continuous: a smooth face (render) runs on through its storeys with no line at each storey's foot;
 * - damp: a darker band with a wavering tide mark at the foot of a ground-storey face;
 * - quoins: dressed stones up every outer end of a face (a corner, a jamb), long and short in turn;
 * - stringCourse: a band of dressed stone along the foot of every so many storeys, dividing a tall face into stages;
 * - framing: a timber frame over a face of infill -- posts by world position, the panels between them and the rails
 *   filled with studs, braces, saltires and arched heads, each panel's infill limewashed a little differently.
 *
 * Pure and deterministic: everything comes from the pieces' own pixels (which part each colour is:
 * WallTrims.parts), the storey stack, and hashes of world position. The same wall always gets the
 * same trims, building beside it never reshuffles them, and a plant's lower part never changes when
 * storeys are built over it.
 */
import type { RgbaImage } from "./autotile_compose"
import { coversWallBelow } from "./autotile_wall"
import type { WallAutotileDefinition, WallTrimFill, WallTrims } from "./autotile_wall"
// The data (a wall definition's `trims`) lives with the wall kind, whose validator checks it.
export type { WallTrimFill, WallTrimFraming, WallTrimFramingPanel, WallTrimMasonry, WallTrimPalette, WallTrims } from "./autotile_wall"
export { validateWallTrims } from "./autotile_wall"

const T = 32

// ---------------------------------------------------------------------------------------------
// The scene trims are drawn in
// ---------------------------------------------------------------------------------------------

export interface WallTrimPiece {
    /** The wall art drawn at this cell on this storey, 32x32. */
    image: RgbaImage
    /** The wall cell it belongs to: its own cell for a face or a north-south run, the cell south for a top. */
    parent: { x: number; y: number }
    /** The material of the wall at `parent`. */
    definition: WallAutotileDefinition
}

export interface WallTrimStorey {
    /** The cells of this storey a wall stands on. */
    cells(): { x: number; y: number }[]
    /** The wall art drawn at a cell of this storey, if a wall drew it. */
    piece(x: number, y: number): WallTrimPiece | undefined
    /** The material of the wall standing at a cell of this storey. */
    wall(x: number, y: number): WallAutotileDefinition | undefined
    /** Whether this storey has a floor at a cell. Without it, no cell counts as floored. */
    floored?(x: number, y: number): boolean
}

/** What lies at a cell of the bottom storey: earth things grow in, hard ground, a floor, water. */
export type WallTrimGround = "soil" | "hard" | "floor" | "water"

export interface WallTrimStack {
    /** Bottom first: storey k is drawn `rise` x k px above the bottom one. */
    storeys: WallTrimStorey[]
    rise: number
    ground(x: number, y: number): WallTrimGround
}

/** A gateway a storey cuts an arch over: its run of cells, and the material of the wall over it. */
export interface ArchGateway {
    x0: number
    x1: number
    definition: WallAutotileDefinition
}

/**
 * The gateway under storey `here` at cell row y that the cell x opens over or flanks, when `here` cuts an arch over it:
 * a run of cells with no wall on `below`, walled at both ends there, every cell walled over on `here` with a material that
 * arches a run that wide, and no floor of `here` in front of it (a doorway seen from inside the room above it shows that
 * room's wall, not an arch). The painter cuts the arch by this; the client shows the passage through its opening.
 */
export const archGateway = (below: Pick<WallTrimStorey, "wall">, here: Pick<WallTrimStorey, "wall" | "floored">, x: number, y: number):
    ArchGateway | undefined => {
    const open = (cx: number) => !below.wall(cx, y) && !!here.wall(cx, y)
    let start = x
    if (!open(x)) {
        if (open(x + 1)) start = x + 1
        else if (open(x - 1)) start = x - 1
        else return undefined
    }
    let x0 = start, x1 = start
    while (open(x0 - 1) && start - x0 < 6) x0--
    while (open(x1 + 1) && x1 - start < 6) x1++
    if (!below.wall(x0 - 1, y) || !below.wall(x1 + 1, y)) return undefined
    for (let cx = x0; cx <= x1; cx++) if (here.floored?.(cx, y + 1)) return undefined
    const definition = here.wall(start, y)!
    const arch = definition.trims?.arch
    if (!arch || x1 - x0 + 1 > arch.maxSpan) return undefined
    return { x0, x1, definition }
}

/** Art a trim draws past its cell: drawn at (dx, dy) from the cell's top-left, sorted as the cell's wall. */
export interface WallTrimOverflow {
    image: RgbaImage
    dx: number
    dy: number
}

export interface TrimmedWallPiece {
    image: RgbaImage
    overflow: WallTrimOverflow[]
}

// ---------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------

/** A stable number in [0, 1) for a tuple of integers. */
export const trimHash = (...values: number[]): number => {
    let h = 0x811c9dc5
    for (const v of values) {
        h = Math.imul(h ^ (v | 0), 0x01000193)
        h ^= h >>> 15
        h = Math.imul(h, 0x2c1b3c6d)
    }
    h ^= h >>> 13
    h = Math.imul(h, 0x5bd1e995)
    h ^= h >>> 15
    return (h >>> 0) / 4294967296
}

/** A seeded generator for the walks a plant takes. */
const seeded = (seed: number) => {
    let a = Math.floor(seed * 4294967296) >>> 0
    return () => {
        a = (a + 0x6d2b79f5) >>> 0
        let t = a
        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

const SALT = { column: 11, split: 12, tone: 13, speck: 14, coping: 21, set: 22, weed: 31, streak: 41, streakLength: 42,
    foot: 51, footKind: 52, footSize: 53, moss: 54, root: 61, rootX: 62, rootReach: 63, plant: 64, leaf: 65, fork: 66,
    damp: 71, quoin: 81, course: 91, bay: 101, fill: 102, side: 103, wash: 104, grain: 105, kink: 106, bloom: 111 }

type Rgb = [number, number, number]
const rgb = (hex: string): Rgb => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)) as Rgb
const key = (r: number, g: number, b: number) => (r << 16) | (g << 8) | b
const keyOf = (hex: string) => key(...rgb(hex))

const copyImage = (image: RgbaImage): RgbaImage => ({ width: image.width, height: image.height, data: new Uint8Array(image.data) })
const blank = (width: number, height: number): RgbaImage => ({ width, height, data: new Uint8Array(width * height * 4) })
const alphaAt = (image: RgbaImage, x: number, y: number) => (x < 0 || y < 0 || x >= image.width || y >= image.height ? 0 : image.data[(y * image.width + x) * 4 + 3])
const colourAt = (image: RgbaImage, x: number, y: number) => {
    const i = (y * image.width + x) * 4
    return key(image.data[i], image.data[i + 1], image.data[i + 2])
}
const rgbAt = (image: RgbaImage, x: number, y: number): Rgb => {
    const i = (y * image.width + x) * 4
    return [image.data[i], image.data[i + 1], image.data[i + 2]]
}
const put = (image: RgbaImage, x: number, y: number, c: Rgb) => {
    if (x < 0 || y < 0 || x >= image.width || y >= image.height) return
    const i = (y * image.width + x) * 4
    image.data[i] = c[0]
    image.data[i + 1] = c[1]
    image.data[i + 2] = c[2]
    image.data[i + 3] = 255
}
const clear = (image: RgbaImage, x: number, y: number) => {
    if (x < 0 || y < 0 || x >= image.width || y >= image.height) return
    image.data.fill(0, (y * image.width + x) * 4, (y * image.width + x) * 4 + 4)
}

type Part = "face" | "top" | "coping" | "frame" | "other" | "void"

/** A material's trims with its colours parsed once. */
interface Kit {
    trims: WallTrims
    face: Set<number>
    top: Set<number>
    coping: Set<number>
    /** What framing members may be laid over besides the face. */
    over: Set<number>
    /** A frame's timber (palette.timber, when the material is framed): creepers climb it as they climb a face. */
    frame: Set<number>
    c: {
        outline: Rgb; joint: Rgb; stem: Rgb
        stone: Rgb[]; top: Rgb[]; dressed: Rgb[]; moss: Rgb[]; grass: Rgb[]; leaves: Rgb[]; timber: Rgb[]; flowers: Rgb[]
    }
}

const kits = new WeakMap<WallTrims, Kit>()
const kitOf = (definition: WallAutotileDefinition | undefined): Kit | undefined => {
    const trims = definition?.trims
    if (!trims) return undefined
    let kit = kits.get(trims)
    if (!kit) {
        const p = trims.palette
        kit = {
            trims,
            face: new Set(trims.parts.face.map(keyOf)),
            top: new Set(trims.parts.top.map(keyOf)),
            coping: new Set((trims.parts.coping || []).map(keyOf)),
            over: new Set((trims.framing?.over || []).map(keyOf)),
            frame: new Set(trims.framing ? (p.timber || []).map(keyOf) : []),
            c: {
                outline: rgb(p.outline), joint: rgb(p.joint), stem: rgb(p.stem),
                stone: p.stone.map(rgb), top: p.top.map(rgb), dressed: p.dressed.map(rgb), moss: p.moss.map(rgb), grass: p.grass.map(rgb), leaves: p.leaves.map(rgb),
                timber: (p.timber || []).map(rgb), flowers: (p.flowers || []).map(rgb),
            },
        }
        kits.set(trims, kit)
    }
    return kit
}

const partOf = (kit: Kit | undefined, image: RgbaImage, x: number, y: number): Part => {
    if (alphaAt(image, x, y) < 128) return "void"
    if (!kit) return "other"
    const c = colourAt(image, x, y)
    return kit.face.has(c) ? "face" : kit.top.has(c) ? "top" : kit.coping.has(c) ? "coping" : kit.frame.has(c) ? "frame" : "other"
}

// ---------------------------------------------------------------------------------------------
// The painter
// ---------------------------------------------------------------------------------------------

interface PlantPaint { x: number; y: number; c: Rgb }

/**
 * One step of a timber frame over one cell: `post` paints any art (a post runs through the rails), `member` only the
 * face or what framing is laid over, `shade` and `tone` scale a face pixel.
 */
interface FrameOp { px: number; py: number; kind: "post" | "member" | "shade" | "tone"; c?: Rgb; f?: number }

/**
 * Draws a stack's trims. One per stack and state of its walls: everything it computes is kept, so
 * each storey's renderer asks the same painter for its cells.
 */
export class WallTrimPainter {
    private dressedPieces = new Map<string, RgbaImage | null>()
    private pieces = new Map<string, WallTrimPiece | null>()
    private lattices = new Map<string, number[]>()
    private plants?: Map<string, PlantPaint[]>
    private frames = new Map<string, Map<number, FrameOp[]>>()
    private storeyCells = new Map<number, { x: number; y: number }[]>()

    constructor(readonly stack: WallTrimStack) {}

    /** A storey's piece at a cell, asked of the stack once. */
    private pieceAt(k: number, x: number, y: number): WallTrimPiece | undefined {
        const id = `${k},${x},${y}`
        let piece = this.pieces.get(id)
        if (piece === undefined) {
            piece = this.stack.storeys[k]?.piece(x, y) ?? null
            this.pieces.set(id, piece)
        }
        return piece ?? undefined
    }

    /**
     * One cell's art with its trims: over `image` (the art the caller draws there; by default the
     * stack's piece), and what spills past the cell. Undefined when no trim of its material applies.
     */
    trim(k: number, x: number, y: number, image?: RgbaImage): TrimmedWallPiece | undefined {
        const piece = this.pieceAt(k, x, y)
        const kit = kitOf(piece?.definition)
        if (!piece || !kit) return undefined
        const out = this.dress(k, x, y, piece, kit, copyImage(image ?? piece.image))
        ;(this.plantPaints().get(`${k},${x},${y}`) || []).forEach(p => put(out, p.x, p.y, p.c))
        const overflow = [...this.foot(k, x, y, piece, kit), ...this.spurs(k, x, y, piece, kit)]
        return { image: out, overflow }
    }

    /** Whether a cell's art carries any trim (cheap: no painting). */
    applies(k: number, x: number, y: number): boolean {
        return !!kitOf(this.pieceAt(k, x, y)?.definition)
    }

    // -----------------------------------------------------------------------------------------
    // Dressing one piece: masonry, streaks, the arch, coping, weeds, moss at the foot
    // -----------------------------------------------------------------------------------------

    private dressed(k: number, x: number, y: number): RgbaImage | undefined {
        const id = `${k},${x},${y}`
        if (this.dressedPieces.has(id)) return this.dressedPieces.get(id) ?? undefined
        const piece = this.pieceAt(k, x, y)
        const kit = kitOf(piece?.definition)
        const image = piece ? (kit ? this.dress(k, x, y, piece, kit, copyImage(piece.image)) : piece.image) : undefined
        this.dressedPieces.set(id, image ?? null)
        return image
    }

    private dress(k: number, x: number, y: number, piece: WallTrimPiece, kit: Kit, image: RgbaImage): RgbaImage {
        const face = y === piece.parent.y && x === piece.parent.x
        const top = y === piece.parent.y - 1 || (face && !this.hasFace(image, kit))
        // A parapet standing on it (merlons) leaves its top uncovered.
        const uncovered = !coversWallBelow(this.stack.storeys[k + 1]?.wall(piece.parent.x, piece.parent.y))
        if (kit.trims.continuous && (face ? this.stack.storeys[k - 1]?.wall(x, y)?.id === piece.definition.id : top && !uncovered)) this.continuous(kit, image)
        if (kit.trims.framing) this.framing(k, x, y, piece.definition, kit, image)
        if (face && kit.trims.masonry) this.masonry(k, x, y, kit, image)
        if (face && uncovered && kit.trims.streaks) this.streaks(k, x, kit, image)
        if (face && kit.trims.damp && k === 0) this.damp(x, y, kit, image)
        if (face && kit.trims.foot && k === 0 && this.stack.ground(x, y + 1) === "soil") this.moss(x, y, kit, image)
        if (face && kit.trims.stringCourse) this.stringCourse(k, x, y, piece, kit, image)
        if (kit.trims.quoins) this.quoins(k, x, y, kit, image)
        if (top && uncovered && kit.trims.coping) this.coping(k, x, y, kit, image)
        if (top && uncovered && kit.trims.weeds) this.weeds(k, x, y, kit, image)
        this.arch(k, x, y, piece, kit, image)
        return image
    }

    private hasFace(image: RgbaImage, kit: Kit): boolean {
        for (let y = 0; y < T; y++) for (let x = 0; x < T; x++) if (partOf(kit, image, x, y) === "face") return true
        return false
    }

    /** The column starts of a lattice along one row of one storey, from world x 0, by a hash of each column. */
    private lattice(id: string, seed: number[], widths: number[], upTo: number): number[] {
        let starts = this.lattices.get(id)
        if (!starts) {
            starts = [0]
            this.lattices.set(id, starts)
        }
        while (starts[starts.length - 1] <= upTo) {
            const i = starts.length - 1
            const w = widths[Math.floor(trimHash(...seed, i) * widths.length)]
            starts.push(starts[i] + w)
        }
        return starts
    }

    private masonry(k: number, x: number, y: number, kit: Kit, image: RgbaImage) {
        const spec = k > 0 ? kit.trims.masonry!.upper ?? kit.trims.masonry!.ground : kit.trims.masonry!.ground
        const [r0, r1] = spec.rows
        const X0 = x * T
        const starts = this.lattice(`m:${k}:${y}:${spec.widths.join(".")}`, [SALT.column, k, y], spec.widths, X0 + T)
        const { stone, joint } = kit.c
        const paint = (px: number, py: number, c: Rgb) => partOf(kit, image, px, py) === "face" && put(image, px, py, c)
        for (let i = 0; i + 1 < starts.length; i++) {
            const c0 = starts[i], c1 = starts[i + 1] - 1 // c1: the column's joint
            if (c1 < X0 || c0 > X0 + T - 1) continue
            const split = spec.splits[Math.floor(trimHash(SALT.split, k, y, i) * spec.splits.length)]
            let yy = r0, s = 0
            while (yy <= r1) {
                const h = s < split.length - 1 ? split[s] : r1 - yy + 2
                const sy0 = yy, sy1 = Math.min(r1, yy + h - 2)
                const tone = 1 + Math.floor(trimHash(SALT.tone, k, y, i, s) * 3) // 1..3
                for (let X = Math.max(c0, X0); X <= Math.min(c1, X0 + T - 1); X++) {
                    const px = X - X0
                    for (let py = sy0; py <= Math.min(r1, sy1 + 1); py++) {
                        if (X === c1 || py === sy1 + 1) {
                            paint(px, py, joint)
                            continue
                        }
                        const left = X === c0, right = X === c1 - 1, upper = py === sy0, lower = py === sy1
                        if ((left || right) && (upper || lower)) paint(px, py, joint)
                        else if (upper) paint(px, py, right ? stone[3] : stone[4])
                        else if (left) paint(px, py, lower ? stone[2] : stone[3])
                        else if (lower || right) paint(px, py, stone[Math.max(0, tone - 1)])
                        else {
                            const u = (py - sy0) / Math.max(1, sy1 - sy0) + ((X - c0) / Math.max(1, c1 - 1 - c0)) * 0.6
                            let t = u < 0.3 ? tone + 1 : u > 1.25 ? tone - 1 : tone
                            const speck = trimHash(SALT.speck, X, py, k, y)
                            if (speck < 0.04) t += speck < 0.02 ? 1 : -1
                            paint(px, py, stone[Math.max(0, Math.min(4, t))])
                        }
                    }
                }
                yy = sy1 + 2
                s++
            }
        }
    }

    private streaks(k: number, x: number, kit: Kit, image: RgbaImage) {
        const share = kit.trims.streaks!.share
        for (let px = 0; px < T; px++) {
            const X = x * T + px
            if (trimHash(SALT.streak, k, X) >= share) continue
            const length = 3 + Math.floor(trimHash(SALT.streakLength, k, X) * 8)
            // From the first face row down, darker where it starts.
            let first = -1
            for (let py = 0; py < T && first < 0; py++) if (partOf(kit, image, px, py) === "face") first = py
            if (first < 0) continue
            for (let d = 0; d < length; d++) {
                const py = first + d
                if (partOf(kit, image, px, py) !== "face") continue
                put(image, px, py, kit.c.stone[d < length / 2 ? 0 : 1])
            }
        }
    }

    private moss(x: number, y: number, kit: Kit, image: RgbaImage) {
        const share = kit.trims.foot!.moss
        for (let spot = 0; spot < 3; spot++) {
            if (trimHash(SALT.moss, x, y, spot) >= share) continue
            const cx = Math.floor(trimHash(SALT.moss, x, y, spot, 1) * T)
            const r = 3 + Math.floor(trimHash(SALT.moss, x, y, spot, 2) * 4)
            for (let py = T - 1 - 10; py < T - 1; py++) for (let px = cx - r - 2; px <= cx + r + 2; px++) {
                if (partOf(kit, image, px, py) !== "face") continue
                const d = ((px - cx) / (r + 2)) ** 2 + ((py - (T - 2)) / 9) ** 2
                if (d < 1 && trimHash(SALT.moss, x * T + px, py, spot) < 0.75 - d * 0.55) put(image, px, py, kit.c.moss[Math.floor(trimHash(SALT.moss, px, py, 7) * kit.c.moss.length)])
            }
        }
    }

    /**
     * The line at the foot of a piece that runs on into more of this face (a face standing on this material, a top the
     * storey above covers with more of its face): each pixel of it painted as the face pixel above it.
     */
    private continuous(kit: Kit, image: RgbaImage) {
        const outline = key(...kit.c.outline)
        for (let px = 0; px < T; px++) {
            if (alphaAt(image, px, T - 1) < 128 || colourAt(image, px, T - 1) !== outline || partOf(kit, image, px, T - 2) !== "face") continue
            put(image, px, T - 1, rgbAt(image, px, T - 2))
        }
    }

    /** Rising damp: from the ground up to a tide mark that wanders along the wall by world x, the face a shade darker. */
    private damp(x: number, y: number, kit: Kit, image: RgbaImage) {
        const [least, most] = kit.trims.damp!.height
        const s = kit.c.stone
        for (let px = 0; px < T; px++) {
            const X = x * T + px
            // A smooth wave through a hashed height every 8px, and now and then a pixel's jitter.
            const i = Math.floor(X / 8), f = (X - i * 8) / 8, u = f * f * (3 - 2 * f)
            const a = trimHash(SALT.damp, i, y), b = trimHash(SALT.damp, i + 1, y)
            const height = Math.round(least + (most - least) * (a + (b - a) * u)) + (trimHash(SALT.damp, X, y, 1) < 0.2 ? 1 : 0)
            const mark = T - 1 - height
            for (let py = Math.max(0, mark); py < T; py++) {
                if (partOf(kit, image, px, py) !== "face") continue
                put(image, px, py, py === mark ? s[0] : trimHash(SALT.damp, X, py, 2) < 0.2 ? s[2] : s[1])
            }
            // Salt blooms just over the mark.
            if (mark > 0 && partOf(kit, image, px, mark - 1) === "face" && trimHash(SALT.damp, X, y, 3) < 0.16) put(image, px, mark - 1, s[4])
        }
    }

    /** Whether storey k at (x, y) carries a string course: every so many storeys, standing on this material. */
    private coursed(k: number, x: number, y: number, kit: Kit, id: string): boolean {
        const every = kit.trims.stringCourse?.every
        return !!every && k > 0 && k % every === 0 && this.stack.storeys[k]?.wall(x, y)?.id === id && this.stack.storeys[k - 1]?.wall(x, y)?.id === id
    }

    /**
     * The string course over a face's foot (its last four rows: a lit arris, the band, its shaded underside, joints
     * by world x), and the line of shade it throws on the top row of the face below.
     */
    private stringCourse(k: number, x: number, y: number, piece: WallTrimPiece, kit: Kit, image: RgbaImage) {
        const d = kit.c.dressed, id = piece.definition.id
        if (this.coursed(k, x, y, kit, id)) {
            const rows: Rgb[] = [d[3], d[2], d[2], d[1]]
            for (let px = 0; px < T; px++) {
                const X = x * T + px, joint = trimHash(SALT.course, Math.floor(X / 3), y, k) < 0.06 && trimHash(SALT.course, X, y, k) < 0.5
                rows.forEach((c, i) => {
                    const py = T - rows.length + i
                    if (partOf(kit, image, px, py) === "face") put(image, px, py, joint && i > 0 ? d[0] : c)
                })
            }
        }
        if (this.coursed(k + 1, x, y, kit, id)) for (let px = 0; px < T; px++) if (partOf(kit, image, px, 0) === "face") put(image, px, 0, kit.c.stone[1])
    }

    /**
     * Quoins up every outer end of a face in this piece: where past the face there is the silhouette's outline and
     * then nothing (an outer corner, a jamb), not another wall (an inner corner, a tee). The courses run by the
     * stack's drawn rows, so they carry on from storey to storey; the stones stay inside this piece.
     */
    private quoins(k: number, x: number, y: number, kit: Kit, image: RgbaImage) {
        const { long, short, course } = kit.trims.quoins!
        const d = kit.c.dressed
        const clear = (px: number, py: number) => {
            const cell = x + Math.floor(px / T)
            const art = cell === x ? image : this.pieceAt(k, cell, y)?.image
            return !art || alphaAt(art, px - (cell - x) * T, py) < 128
        }
        const faceAt = (px: number, py: number) => {
            if (px >= 0 && px < T) return partOf(kit, image, px, py) === "face"
            const cell = x + Math.floor(px / T), piece = this.pieceAt(k, cell, y)
            return !!piece && partOf(kitOf(piece.definition), piece.image, px - (cell - x) * T, py) === "face"
        }
        // An end at px (the face's last pixel), facing `step`: past it, the outline and then nothing, or nothing at all.
        const outerEnd = (px: number, step: number, py: number) => !faceAt(px + step, py) && (clear(px + step, py) || clear(px + 2 * step, py))
        for (let py = 0; py < T; py++) {
            const ends: [number, number][] = []
            for (let px = 0; px < T; px++) {
                if (partOf(kit, image, px, py) !== "face") continue
                if (outerEnd(px, -1, py)) ends.push([px, 1])
                if (outerEnd(px, 1, py)) ends.push([px, -1])
            }
            if (!ends.length) continue
            const Ys = y * T + py - k * this.stack.rise
            const c = Math.floor(Ys / course), r = Ys - c * course
            const width = c % 2 === 0 ? long : short
            // Each stone inward from its end: a lit top arris, the outer arris lit on a west end and shaded on an east
            // one, a shaded foot, its bed joint and its inner edge where the face meets it.
            ends.forEach(([from, step]) => {
                for (let i = 0; i < width; i++) {
                    const px = from + i * step
                    if (px < 0 || px >= T) break
                    if (partOf(kit, image, px, py) !== "face") continue
                    let colour: Rgb
                    if (r === course - 1 || i === width - 1) colour = d[0]
                    else if (r === 0) colour = d[3]
                    else if (i === 0) colour = step > 0 ? d[3] : d[1]
                    else if (r === course - 2) colour = d[1]
                    else colour = trimHash(SALT.quoin, x * T + px, Ys) < 0.06 ? d[1] : d[2]
                    put(image, px, py, colour)
                }
            })
        }
    }

    // -----------------------------------------------------------------------------------------
    // Timber framing
    // -----------------------------------------------------------------------------------------

    private framing(k: number, x: number, y: number, definition: WallAutotileDefinition, kit: Kit, image: RgbaImage) {
        const ops = this.frameRow(k, y, definition, kit).get(x)
        if (!ops) return
        // What each pixel is, as drawn before the frame; the timbers first, then the infill's shade and tone, each
        // once, on what the timbers left.
        const before = copyImage(image)
        const timbered = new Uint8Array(T * T), factor = new Float32Array(T * T).fill(1), shaded = new Uint8Array(T * T), toned = new Uint8Array(T * T)
        ops.forEach(op => {
            const part = partOf(kit, before, op.px, op.py), i = op.py * T + op.px
            if (op.kind === "post") {
                if (part === "void") return
                put(image, op.px, op.py, op.c!)
                timbered[i] = 1
            } else if (op.kind === "member") {
                if (part !== "face" && !kit.over.has(colourAt(before, op.px, op.py))) return
                put(image, op.px, op.py, op.c!)
                timbered[i] = 1
            } else if (part === "face") {
                const seen = op.kind === "shade" ? shaded : toned
                if (seen[i]) return
                seen[i] = 1
                factor[i] *= op.f!
            }
        })
        for (let i = 0; i < T * T; i++) {
            if (timbered[i] || factor[i] === 1) continue
            const px = i % T, py = Math.floor(i / T)
            const [r, g, b] = rgbAt(before, px, py), f = factor[i]
            put(image, px, py, [Math.min(255, Math.round(r * f)), Math.min(255, Math.round(g * f)), Math.min(255, Math.round(b * f))])
        }
    }

    /**
     * The frame along one cell row of one storey, cell by cell: worked out once over the whole of each run of wall, so a
     * panel that spans cells is filled the same from both sides, from the stack's pieces (what is face where).
     */
    private frameRow(k: number, y: number, definition: WallAutotileDefinition, kit: Kit): Map<number, FrameOp[]> {
        const id = `${k},${y},${definition.id}`
        const known = this.frames.get(id)
        if (known) return known
        const out = new Map<number, FrameOp[]>()
        this.frames.set(id, out)
        const storey = this.stack.storeys[k]
        if (!storey) return out
        const framing = kit.trims.framing!
        const tm = kit.c.timber
        if (tm.length < 5) return out
        // The cells of this row with art: walls on it and the tops of the walls south of it.
        let cells = this.storeyCells.get(k)
        if (!cells) {
            cells = storey.cells()
            this.storeyCells.set(k, cells)
        }
        const xs = new Set<number>()
        cells.forEach(c => (c.y === y || c.y === y + 1) && xs.add(c.x))
        const sorted = [...xs].sort((a, b) => a - b)
        const groups: [number, number][] = []
        sorted.forEach(cx => {
            const last = groups[groups.length - 1]
            if (last && last[1] === cx - 1) last[1] = cx
            else groups.push([cx, cx])
        })
        const emit = (X: number, py: number, op: Omit<FrameOp, "px" | "py">) => {
            const cx = Math.floor(X / T)
            const list = out.get(cx) ?? []
            list.push({ px: X - cx * T, py, ...op })
            out.set(cx, list)
        }
        const W = framing.width
        const panels = framing.panels
        groups.forEach(([g0, g1]) => {
            const X0 = g0 * T, X1 = g1 * T + T - 1, n = X1 - X0 + 1
            // Which pixels of the run's rows are infill a member may be laid over: this material's face, or `over`.
            const infill = Array.from({ length: T }, () => new Uint8Array(n))
            for (let cx = g0; cx <= g1; cx++) {
                const piece = this.pieceAt(k, cx, y)
                if (!piece || piece.definition.id !== definition.id) continue
                for (let py = 0; py < T; py++) for (let px = 0; px < T; px++) {
                    if (partOf(kit, piece.image, px, py) === "face" || (alphaAt(piece.image, px, py) >= 128 && kit.over.has(colourAt(piece.image, px, py)))) infill[py][cx * T + px - X0] = 1
                }
            }
            const open = (X: number, py: number) => X >= X0 && X <= X1 && infill[py][X - X0] === 1
            const panelOpen = (X: number) => panels.every(p => {
                for (let py = p.rows[0]; py <= p.rows[1]; py++) if (!open(X, py)) return false
                return true
            })
            // Posts by the lattice, from world x 0 and the same on every storey: each stands only clear of other art.
            const starts = this.lattice(`f:${y}:${framing.bays.join(".")}`, [SALT.bay, y], framing.bays, X1 + T)
            const posted = new Uint8Array(n)
            starts.forEach((s, i) => {
                const c0 = s - Math.floor(W / 2), c1 = c0 + W - 1
                if (c1 + 2 < X0 || c0 - 2 > X1) return
                for (let X = c0 - 2; X <= c1 + 2; X++) if (!panelOpen(X)) return
                for (let X = c0; X <= c1; X++) posted[X - X0] = 1
                const colour = (X: number) => (X === c0 ? tm[3] : X === c1 ? tm[1] : tm[2])
                for (let py = framing.posts[0]; py <= framing.posts[1]; py++) for (let X = c0; X <= c1; X++) emit(X, py, { kind: "post", c: colour(X) })
                // Its pegs where the rails between the panels are tenoned into it, and its shadow on the infill.
                panels.slice(1).forEach((p, j) => emit(c0 + 1, Math.floor((panels[j].rows[1] + p.rows[0]) / 2), { kind: "post", c: tm[0] }))
                panels.forEach(p => {
                    for (let py = p.rows[0]; py <= p.rows[1]; py++) emit(c1 + 1, py, { kind: "shade", f: 0.86 })
                })
                // Now and then a check, a split along the grain.
                if (trimHash(SALT.grain, s, y) < 0.3) {
                    const gx = c0 + 1 + Math.floor(trimHash(SALT.grain, s, y, 1) * (W - 2))
                    const g0 = framing.posts[0] + 1 + Math.floor(trimHash(SALT.grain, s, y, 2) * 6), length = 3 + Math.floor(trimHash(SALT.grain, s, y, 3) * 5)
                    for (let py = g0; py < Math.min(framing.posts[1], g0 + length); py++) emit(gx, py, { kind: "post", c: tm[1] })
                }
                void i
            })
            // The panels between posts and rails: each a run of columns open from its head to its foot.
            panels.forEach((panel, pi) => {
                const [a, b] = panel.rows
                const h = b - a + 1
                let X = X0, previous: WallTrimFill | undefined
                while (X <= X1) {
                    const columnOpen = (cX: number) => {
                        if (cX > X1 || posted[cX - X0]) return false
                        for (let py = a; py <= b; py++) if (!open(cX, py)) return false
                        return true
                    }
                    if (!columnOpen(X)) {
                        X++
                        continue
                    }
                    let x1 = X
                    while (columnOpen(x1 + 1)) x1++
                    const x0 = X, w = x1 - x0 + 1
                    X = x1 + 1
                    // Its limewash, a shade of its own.
                    const tones = framing.tones ?? 0
                    if (tones > 0) {
                        const t = Math.floor(trimHash(SALT.wash, k, y, pi, x0) * 3) - 1
                        if (t) for (let py = a; py <= b; py++) for (let cX = x0; cX <= x1; cX++) emit(cX, py, { kind: "tone", f: 1 + t * tones })
                    }
                    const fill = this.pickFill(panel.fills, w, h, previous, [k, y, pi, x0])
                    previous = fill
                    this.fillPanel(fill, x0, x1, a, b, [k, y, pi, x0], tm, emit)
                }
            })
        })
        return out
    }

    /** A fill for a panel w x h: by weight among those it is wide enough for, not the same as the panel before it (bare infill aside). */
    private pickFill(fills: Partial<Record<WallTrimFill, number>>, w: number, h: number, previous: WallTrimFill | undefined, seed: number[]): WallTrimFill {
        const fits = (fill: WallTrimFill) => {
            switch (fill) {
                case "plain": return true
                case "stud": return w >= 10
                case "studs": return w >= 14
                case "brace": return w >= h + 2
                case "chevron": return w >= 2 * h + 2
                case "cross": return w >= 8 && h >= 5 && w <= 2 * h + 6
                case "arched": return w >= 12 && h >= 6
            }
        }
        const choices = (Object.entries(fills) as [WallTrimFill, number][]).filter(([fill, weight]) => weight > 0 && fits(fill))
        const pick = (salt: number, list: [WallTrimFill, number][]) => {
            const total = list.reduce((sum, [, weight]) => sum + weight, 0)
            let r = trimHash(SALT.fill, salt, ...seed) * total
            for (const [fill, weight] of list) if ((r -= weight) < 0) return fill
            return list[list.length - 1]?.[0] ?? "plain"
        }
        const first = pick(0, choices)
        if (first === previous && first !== "plain") {
            const others = choices.filter(([fill]) => fill !== first)
            if (others.length) return pick(1, others)
        }
        return first
    }

    /** Lays one fill over a panel (x0..x1 world px, rows a..b): members as ops, their shadows on the infill. */
    private fillPanel(fill: WallTrimFill, x0: number, x1: number, a: number, b: number, seed: number[], tm: Rgb[], emit: (X: number, py: number, op: Omit<FrameOp, "px" | "py">) => void) {
        const w = x1 - x0 + 1, h = b - a + 1
        const member = (X: number, py: number, c: Rgb) => X >= x0 && X <= x1 && py >= a && py <= b && emit(X, py, { kind: "member", c })
        const shade = (X: number, py: number) => X >= x0 && X <= x1 && py >= a && py <= b && emit(X, py, { kind: "shade", f: 0.86 })
        const stud = (cX: number, salt: number) => {
            // Hewn, not sawn: now and then it steps a pixel part way up.
            const kink = trimHash(SALT.kink, ...seed, salt) < 0.35 ? a + 2 + Math.floor(trimHash(SALT.kink, ...seed, salt, 1) * Math.max(1, h - 4)) : -1
            const side = trimHash(SALT.kink, ...seed, salt, 2) < 0.5 ? -1 : 1
            for (let py = a; py <= b; py++) {
                const c = cX + (kink >= 0 && py < kink ? side : 0)
                member(c, py, tm[3])
                member(c + 1, py, tm[2])
                member(c + 2, py, tm[1])
                shade(c + 3, py)
            }
        }
        // A straight brace with its foot at (fx, b) and its head at (hx, a): three px across, lit on its upper side.
        const brace = (fx: number, hx: number) => {
            for (let py = a; py <= b; py++) {
                const t = h > 1 ? (b - py) / (h - 1) : 0
                const c = Math.round(fx + (hx - fx) * t)
                member(c, py, tm[3])
                member(c + 1, py, tm[2])
                member(c + 2, py, tm[1])
                shade(c + 3, py)
            }
        }
        switch (fill) {
            case "plain":
                return
            case "stud":
                return stud(x0 + Math.floor((w - 3) / 2), 0)
            case "studs": {
                const count = Math.max(1, Math.round(w / 8) - 1)
                for (let i = 1; i <= count; i++) stud(x0 + Math.round((i * w) / (count + 1)) - 1, i)
                return
            }
            case "brace": {
                // Rising from one post at the panel's foot to its head, leaning out at 45 degrees.
                const reach = Math.min(w - 3, h - 1)
                return trimHash(SALT.side, ...seed) < 0.5 ? brace(x0, x0 + reach) : brace(x1 - 2, x1 - 2 - reach)
            }
            case "chevron": {
                const mid = x0 + Math.floor(w / 2)
                brace(x0, mid - 2)
                brace(x1 - 2, mid - 1)
                return
            }
            case "cross":
                brace(x0, x1 - 2)
                brace(x1 - 2, x0)
                return
            case "arched": {
                // The panel's head an arch: timber spandrels from its posts, springing part way down, to a crown just
                // under the head, the infill in the opening shaded along its edge.
                const spring = Math.min(h - 3, Math.max(5, Math.round(h * 0.6))), crown = 2
                const rx = w / 2, ry = spring - crown, xc = x0 + w / 2, yc = a + spring
                for (let py = a; py < a + spring; py++) for (let X = x0; X <= x1; X++) {
                    const dx = (X + 0.5 - xc) / rx, dy = (py + 0.5 - yc) / ry
                    const e = dx * dx + dy * dy
                    if (e >= 1) member(X, py, e < 1.3 ? tm[1] : tm[2])
                    else if (e > 0.78) shade(X, py)
                }
                return
            }
        }
    }

    /** The first opaque row of each pixel column with a top below it: the back edge of a top, where open sky is behind. */
    private backEdge(kit: Kit, image: RgbaImage, px: number): number {
        for (let py = 1; py < 20; py++) {
            if (alphaAt(image, px, py) < 128) continue
            const below = partOf(kit, image, px, py + 1)
            return below === "top" && alphaAt(image, px, py - 1) < 128 ? py : -1
        }
        return -1
    }

    private coping(k: number, x: number, y: number, kit: Kit, image: RgbaImage) {
        const [shortest, longest] = kit.trims.coping!.lengths
        const widths = Array.from({ length: longest - shortest + 1 }, (_, i) => shortest + i)
        const X0 = x * T
        const starts = this.lattice(`c:${k}:${y}:${shortest}-${longest}`, [SALT.coping, k, y], widths, X0 + T)
        const t = kit.c.top
        const edges = Array.from({ length: T }, (_, px) => this.backEdge(kit, image, px))
        for (let i = 0; i + 1 < starts.length; i++) {
            const s0 = starts[i], s1 = starts[i + 1] - 1
            if (s1 < X0 || s0 > X0 + T - 1) continue
            const set = [-2, -1, -1, 0, 0, 1][Math.floor(trimHash(SALT.set, k, y, i) * 6)]
            for (let X = Math.max(s0, X0); X <= Math.min(s1, X0 + T - 1); X++) {
                const px = X - X0, edge = edges[px]
                if (edge < 0) continue
                const shoulder = X === s0 || X === s1
                const lift = set + (shoulder ? 1 : 0) // negative: the stone stands proud
                if (X === s1 && X !== s0) {
                    // The joint to the next stone: a notch down the stone's back edge.
                    clear(image, px, edge)
                    put(image, px, edge + 1, t[0])
                    continue
                }
                if (lift < 0) {
                    put(image, px, edge + lift, t[0])
                    for (let py = edge + lift + 1; py <= edge; py++) put(image, px, py, py === edge + lift + 1 ? t[4] : t[3])
                } else if (lift > 0) {
                    for (let py = edge; py < edge + lift; py++) clear(image, px, py)
                    put(image, px, edge + lift, t[0])
                }
            }
        }
    }

    private weeds(k: number, x: number, y: number, kit: Kit, image: RgbaImage) {
        if (trimHash(SALT.weed, k, x, y) >= kit.trims.weeds!.share) return
        const cx = 4 + Math.floor(trimHash(SALT.weed, k, x, y, 1) * 24)
        const g = kit.c.grass
        for (let b = 0; b < 5; b++) {
            const bx = cx + Math.floor(trimHash(SALT.weed, k, x, y, b, 2) * 7) - 3
            const edge = this.backEdge(kit, image, bx)
            if (edge < 0) continue
            const h = 2 + Math.floor(trimHash(SALT.weed, k, x, y, b, 3) * 4)
            for (let d = 0; d <= h; d++) put(image, bx, edge + 1 - d, g[Math.min(g.length - 1, Math.floor((d / h) * g.length))])
        }
    }

    // -----------------------------------------------------------------------------------------
    // The arch over a gateway
    // -----------------------------------------------------------------------------------------

    /** The gateway under storey k at cell row y that the cell x opens over or flanks (archGateway). */
    private gateway(k: number, x: number, y: number): { x0: number; x1: number; kit: Kit } | undefined {
        const below = this.stack.storeys[k - 1], here = this.stack.storeys[k]
        const gate = below && here ? archGateway(below, here, x, y) : undefined
        const kit = kitOf(gate?.definition)
        return gate && kit ? { x0: gate.x0, x1: gate.x1, kit } : undefined
    }

    private arch(k: number, x: number, y: number, piece: WallTrimPiece, kit: Kit, image: RgbaImage) {
        if (k < 1 || piece.parent.y !== y || piece.parent.x !== x) return
        const gate = this.gateway(k, x, y)
        if (!gate) return
        const { rise, ring } = gate.kit.trims.arch!
        const left = gate.x0 * T, right = (gate.x1 + 1) * T
        const half = (right - left) / 2, mid = left + half
        const hr = half, vr = Math.min(half, rise)
        const d = gate.kit.c.dressed
        for (let py = 0; py < T; py++) for (let px = 0; px < T; px++) {
            const X = x * T + px + 0.5
            const dx = X - mid, dy = T - 0.5 - py // above the springing, the face's bottom
            const inner = (dx / hr) ** 2 + (dy / vr) ** 2
            const outer = (dx / (hr + ring)) ** 2 + (dy / (vr + ring)) ** 2
            if (Math.abs(dx) < hr && inner < 1) {
                // The opening: the passage running back through the wall shows through it, and whoever walks it.
                // Its shade is the sun's (the client draws none of this storey's floor under the arch, and shades
                // the passage as the ground under the storey above).
                clear(image, px, py)
            } else if (outer < 1 && dy >= 0) {
                const angle = Math.atan2(dy * (hr / vr), dx) * (180 / Math.PI)
                const joint = angle % 15 < 1.6 || angle % 15 > 13.4
                const edge = inner < 1.12
                const keystone = Math.abs(dx) < 3.2 && dy > vr - 1
                put(image, px, py, edge ? gate.kit.c.outline : keystone ? (Math.abs(dx) > 2.2 ? d[0] : d[3]) : joint ? d[0] : (outer > 0.86 ? d[1] : dx < 0 ? d[3] : d[2]))
            }
        }
    }

    // -----------------------------------------------------------------------------------------
    // Past the cell: the foot, the spurs
    // -----------------------------------------------------------------------------------------

    private foot(k: number, x: number, y: number, piece: WallTrimPiece, kit: Kit): WallTrimOverflow[] {
        const foot = kit.trims.foot
        if (!foot || k !== 0 || piece.parent.y !== y || piece.parent.x !== x) return []
        const ground = this.stack.ground(x, y + 1)
        if (ground === "floor" || ground === "water") return []
        const image = this.pieceAt(0, x, y)!.image
        // Only along the face's foot: where its bottom row has art.
        const footed = Array.from({ length: T }, (_, px) => alphaAt(image, px, T - 1) >= 128)
        if (!footed.some(Boolean)) return []
        const out = blank(T + 8, 14) // x -4..35, y 24..37 of the cell
        const at = (px: number, py: number) => [px + 4, py - 24] as const
        const stones = kit.c.stone
        for (let spot = 0; spot < 6; spot++) {
            const sx = Math.floor(trimHash(SALT.foot, x, y, spot) * T)
            if (!footed[sx]) continue
            const kind = trimHash(SALT.footKind, x, y, spot)
            if (kind < foot.rubble) {
                const w = 2 + Math.floor(trimHash(SALT.footSize, x, y, spot) * 3), h = 1 + Math.floor(trimHash(SALT.footSize, x, y, spot, 1) * 2)
                const cy = T + Math.floor(trimHash(SALT.footSize, x, y, spot, 2) * 3)
                for (let dy = -h; dy <= h; dy++) for (let dx = -w; dx <= w; dx++) {
                    const dd = (dx / (w + 0.5)) ** 2 + (dy / (h + 0.5)) ** 2
                    if (dd > 1) continue
                    const [ox, oy] = at(sx + dx, cy + dy)
                    const c = dd > 0.6 ? (dy > 0 || dx > 0 ? kit.c.outline : kit.c.top[0]) : dx + dy < -w * 0.5 ? stones[4] : dx + dy > w * 0.4 ? stones[1] : stones[2]
                    put(out, ox, oy, c)
                }
            } else if (kind < foot.rubble + foot.tufts && ground === "soil") {
                const g = kit.c.grass
                for (let b = 0; b < 4; b++) {
                    const bx = sx + Math.floor(trimHash(SALT.footSize, x, y, spot, b) * 7) - 3
                    const h = 3 + Math.floor(trimHash(SALT.footSize, x, y, spot, b, 1) * 5)
                    for (let d = 0; d < h; d++) {
                        const [ox, oy] = at(bx + (d > h / 2 && b % 2 ? 1 : 0), T + 2 - d)
                        put(out, ox, oy, g[Math.min(g.length - 1, Math.floor((d / h) * g.length))])
                    }
                }
            }
        }
        return [{ image: out, dx: -4, dy: 24 }]
    }

    private spurs(k: number, x: number, y: number, piece: WallTrimPiece, kit: Kit): WallTrimOverflow[] {
        const spurs = kit.trims.spurs
        if (!spurs || k !== 0 || piece.parent.y !== y || piece.parent.x !== x) return []
        const storey = this.stack.storeys[0]
        const n = !!storey.wall(x, y - 1), s = !!storey.wall(x, y + 1), e = !!storey.wall(x + 1, y), w = !!storey.wall(x - 1, y)
        const side = n && !s && e && !w ? -1 : n && !s && w && !e ? 1 : 0
        if (!side) return []
        const ground = this.stack.ground(x, y + 1)
        if (ground === "floor" || ground === "water") return []
        const image = piece.image
        // Where the face's outer side stands: its outermost opaque column in its lower rows.
        let edge = side < 0 ? T : -1
        for (let py = 20; py < T; py++) for (let px = 0; px < T; px++) if (alphaAt(image, px, py) >= 128) edge = side < 0 ? Math.min(edge, px) : Math.max(edge, px)
        if (edge < 0 || edge >= T) return []
        const { reach, height } = spurs
        // Outward is west for a south-west corner, east for a south-east one; the spur reaches out `reach` px
        // at its foot, 1 px at its head, and laps 6 px over the face.
        const origin = side < 0 ? edge - reach - 1 : edge - 7
        const rows = height + 4, top = T - height
        const out = blank(reach + 8, rows)
        const s0 = kit.c.stone, d = kit.c.dressed
        for (let r = 0; r < rows; r++) {
            const f = r / (rows - 1)
            const reachHere = Math.round(1 + (reach - 1) * f)
            for (let t = -6; t <= reachHere; t++) {
                const px = edge + side * t
                let c: Rgb
                if (r === rows - 1 || t === reachHere) c = kit.c.outline
                else if (r === 0) c = d[3]
                else if (r % 9 === 8) c = kit.c.joint
                else if (t > 0) c = f < 0.5 ? s0[3] : s0[2]
                else c = t === -6 ? s0[1] : (r + t) % 7 ? s0[2] : s0[3]
                put(out, px - origin, r, c)
            }
        }
        const dx = origin
        return [{ image: out, dx, dy: top }]
    }

    // -----------------------------------------------------------------------------------------
    // Creepers
    // -----------------------------------------------------------------------------------------

    /** The part a pixel of the stack's drawn view is, and whose it is: the topmost storey with art there. */
    private owner(X: number, Ys: number): { k: number; x: number; y: number; px: number; py: number; part: Part } | undefined {
        const { storeys, rise } = this.stack
        for (let k = storeys.length - 1; k >= 0; k--) {
            const Y = Ys + k * rise
            const x = Math.floor(X / T), y = Math.floor(Y / T)
            const image = this.dressed(k, x, y)
            if (!image) continue
            const px = X - x * T, py = Y - y * T
            if (alphaAt(image, px, py) < 128) continue
            const piece = this.pieceAt(k, x, y)
            return { k, x, y, px, py, part: partOf(kitOf(piece?.definition), image, px, py) }
        }
        return undefined
    }

    /** Where a pixel of the drawn view would be drawn when it lands on no art: the topmost storey with a piece at that cell. */
    private host(X: number, Ys: number): { k: number; x: number; y: number; px: number; py: number } | undefined {
        const { storeys, rise } = this.stack
        for (let k = storeys.length - 1; k >= 0; k--) {
            const Y = Ys + k * rise
            const x = Math.floor(X / T), y = Math.floor(Y / T)
            if (this.dressed(k, x, y)) return { k, x, y, px: X - x * T, py: Y - y * T }
        }
        return undefined
    }

    private plantPaints(): Map<string, PlantPaint[]> {
        if (this.plants) return this.plants
        const paints = new Map<string, PlantPaint[]>()
        this.plants = paints
        const ground = this.stack.storeys[0]
        if (!ground) return paints
        const roots = this.roots()
        // A face, or a timber frame over one (a climbing rose takes to the posts and rails).
        const climb = (X: number, Ys: number) => {
            const p = this.owner(X, Ys)?.part
            return p === "face" || p === "frame"
        }
        const holds = (X: number, Ys: number) => {
            const p = this.owner(X, Ys)?.part
            return p === "top" || p === "coping"
        }
        // A band of coping or a top with more face just above it (a moulded offset, a string course, the seam
        // where a storey stands on the one below) is crossed, not hung from.
        const crosses = (X: number, Ys: number) => {
            for (let d = 0; d <= 8; d++) {
                const p = this.owner(X, Ys - d)?.part
                if (!p || p === "void") return false
                if (p === "face") return d > 0
            }
            return false
        }
        const draw = (X: number, Ys: number, c: Rgb) => {
            const at = this.owner(X, Ys) ?? this.host(X, Ys)
            if (!at) return
            const id = `${at.k},${at.x},${at.y}`
            const list = paints.get(id) ?? []
            list.push({ x: at.px, y: at.py, c })
            paints.set(id, list)
        }
        roots.forEach(root => {
            const { kit, reach, spread } = root
            // Each stem walks with its own seed (a branch's from its stem's and where it forks), and each leaf is
            // picked by its position: what grows above a point never changes what grew below it.
            const leaves: { X: number; Ys: number; k: number }[] = []
            const stems: { X: number; Ys: number; lean: number; left: number; seed: number }[] = []
            for (let s = 0; s < 3; s++) {
                const seed = trimHash(SALT.plant, root.X, root.Ys, s)
                const random = seeded(seed)
                stems.push({ X: root.X + Math.floor(random() * 7) - 3, Ys: root.Ys, lean: random() - 0.5, left: Math.floor(reach * (0.7 + random() * 0.3)), seed })
            }
            // From the root the stems first cover whatever lies between the ground and the face (a battered
            // footing, a plinth): up to a dozen pixels over any art, then they must find a face to climb.
            let base = root.Ys
            for (let d = 0; d < 12 && !climb(root.X, base) && this.owner(root.X, base - 1); d++) {
                draw(root.X, base, kit.c.stem)
                if (d % 2 === 0) leaves.push({ X: root.X, Ys: base, k: 0 })
                base--
            }
            if (!climb(root.X, base)) return
            stems.forEach(stem => (stem.Ys = base))
            let guard = 0
            while (stems.length && guard++ < 64) {
                const stem = stems.pop()!
                const random = seeded(trimHash(SALT.plant, Math.floor(stem.seed * 1e9), 1))
                let fx = stem.X, X = stem.X, Ys = stem.Ys, draped = 0, run = 0, left = stem.left, step = 0
                while (left-- > 0) {
                    step++
                    const risen = root.Ys - Ys
                    fx += stem.lean + (random() * 1.2 - 0.6)
                    const width = spread * (0.35 + (1.4 * risen) / Math.max(1, reach))
                    fx = Math.max(root.X - width, Math.min(root.X + width, fx))
                    const nx = Math.round(fx), ny = Ys - 1
                    if (climb(nx, ny) || crosses(nx, ny)) [X, Ys] = [nx, ny]
                    else if (holds(nx, ny) && draped < 6) {
                        [X, Ys] = [nx, ny]
                        draped++
                    } else if (climb(nx - 2, ny) || climb(nx + 2, ny)) {
                        X = climb(nx - 2, ny) ? nx - 2 : nx + 2
                        fx = X
                        Ys = ny
                    } else if (draped && run < 8) {
                        // Over the coping it runs along the top a little, then stops.
                        const side = stem.lean >= 0 ? 1 : -1
                        if (holds(X + side, Ys) || climb(X + side, Ys)) {
                            X += side
                            run++
                        } else break
                    } else break
                    draw(X, Ys, kit.c.stem)
                    if (trimHash(SALT.leaf, X, Ys) < 0.55) leaves.push({ X, Ys, k: (root.Ys - Ys) / Math.max(1, reach) })
                    const fork = trimHash(SALT.fork, Math.floor(stem.seed * 1e9), step)
                    if (left > 12 && fork < 0.035) {
                        const seed = trimHash(SALT.plant, Math.floor(stem.seed * 1e9), step, 2)
                        stems.push({ X, Ys, lean: stem.lean + (seed < 0.5 ? -1 : 1) * (0.3 + seed * 0.5), left: Math.floor(left * (0.4 + fork * 10)), seed })
                    }
                }
            }
            const L = kit.c.leaves
            const shape: [number, number, number][] = [[0, 0, 2], [1, 0, 1], [0, 1, 1], [-1, 0, 2], [0, -1, 3], [1, -1, 4], [1, 1, 0]]
            leaves.forEach(leaf => {
                ;[[-2, 0], [2, -1], [-1, -2], [1, 1], [0, -1], [-2, 1]].forEach(([dx, dy], n) => {
                    if (trimHash(SALT.leaf, leaf.X, leaf.Ys, n) > 0.9 - 0.45 * leaf.k) return
                    shape.forEach(([ox, oy, c]) => {
                        const X = leaf.X + dx + ox, Ys = leaf.Ys + dy + oy
                        const there = this.owner(X, Ys)?.part
                        const low = root.Ys - Ys < 14 // by the root, leaves cover whatever the stem grew over
                        if (there === "face" || there === "frame" || there === "top" || there === "coping" || (low && there === "other") || climb(X, Ys + 1) || climb(X - 1, Ys) || climb(X + 1, Ys)) {
                            if (there !== "other" || low) draw(X, Ys, L[Math.min(L.length - 1, c)])
                        }
                    })
                })
                // A climbing rose flowers among its leaves: a blossom lit at its upper left.
                const bloom = kit.trims.creepers?.bloom ?? 0
                const F = kit.c.flowers
                if (bloom > 0 && F.length >= 3 && trimHash(SALT.bloom, leaf.X, leaf.Ys) < bloom) {
                    const bx = leaf.X + Math.floor(trimHash(SALT.bloom, leaf.X, leaf.Ys, 1) * 3) - 1, by = leaf.Ys - 1
                    const there = this.owner(bx, by)?.part
                    if (there === "face" || there === "frame" || there === "top" || there === "coping") {
                        ;([[0, 0, 2], [1, 0, 1], [0, 1, 1], [1, 1, 0]] as const).forEach(([dx, dy, c]) => draw(bx + dx, by + dy, F[c]))
                    }
                }
            })
        })
        return paints
    }

    /** Where ivy roots: at most one per ground-storey face cell on soil, by a hash of the cell. */
    private roots(): { X: number; Ys: number; kit: Kit; reach: number; spread: number }[] {
        const out: { X: number; Ys: number; kit: Kit; reach: number; spread: number }[] = []
        const ground = this.stack.storeys[0]
        ground.cells().forEach(({ x, y }) => {
            const kit = kitOf(ground.wall(x, y))
            const creepers = kit?.trims.creepers
            if (!kit || !creepers || this.stack.ground(x, y + 1) !== "soil") return
            if (trimHash(SALT.root, x, y) >= creepers.share) return
            const [least, most] = creepers.reach
            out.push({
                X: x * T + 6 + Math.floor(trimHash(SALT.rootX, x, y) * 20), Ys: y * T + T - 2, kit,
                reach: Math.round((least + (most - least) * trimHash(SALT.rootReach, x, y)) * this.stack.rise),
                spread: creepers.spread,
            })
        })
        return out
    }

}
